import { generateGalaxy, galaxyOf, nodesOf, routeDistance } from "./world";
import {
  BALANCE as B,
  FACTIONS,
  HOME,
  PREFERENCES,
  PREFERRED_ROLE,
  maxMoves,
  rank,
} from "./data";
import {
  combatShip,
  combatBastion,
  effectivePower,
  shipMax,
  simulateRound,
  retreatSalvo,
} from "./combat";
import { clone, random, seedHash, shuffled } from "./rng";
import { updateIntel, visibleSystems } from "./fog";
import type {
  Ability,
  Building,
  CombatSide,
  FactionId,
  Fleet,
  GameAction,
  GameState,
  Role,
  Ship,
  ShipClass,
  Stance,
  Technology,
  BattleReport,
} from "./types";
export class RuleError extends Error {
  constructor(public key: string) {
    super(key);
    this.name = "RuleError";
  }
}
function check(x: unknown, key: string): asserts x {
  if (!x) throw new RuleError(key);
}
export const actor = (g: GameState) => g.order[g.actorIndex];
const nextId = (g: GameState, p: string) => `${p}-${g.nextId++}`;
export function makeShip(g: GameState, type: ShipClass, f: FactionId): Ship {
  const m = shipMax(type, f, g.factions[f].tech.shields);
  return { id: nextId(g, "ship"), type, hull: m.hull, shields: m.shields };
}
export function createGame(o: {
  faction: FactionId;
  role: Role;
  opponents: number;
  difficulty: GameState["difficulty"];
  seed: string;
  tutorial?: boolean;
}): GameState {
  const rng = { rng: seedHash(o.seed) },
    order = [
      o.faction,
      ...shuffled(
        FACTIONS.filter((f) => f !== o.faction),
        rng,
      ).slice(0, Math.min(5, Math.max(1, o.opponents))),
    ],
    g: GameState = {
      schemaVersion: 1,
      version: "1.2.0",
      seed: o.seed,
      galaxy: generateGalaxy(o.seed, order),
      rng: rng.rng,
      nextId: 1,
      player: o.faction,
      difficulty: o.difficulty,
      turn: 1,
      order,
      actorIndex: 0,
      actorActions: 0,
      phase: "map",
      factions: {} as GameState["factions"],
      systems: {},
      fleets: [],
      battle: null,
      reports: [],
      ledger: [],
      tutorial: o.tutorial ? 0 : -1,
    };
  for (const id of FACTIONS)
    g.factions[id] = {
      id,
      credits: 260,
      rp: 0,
      charge: 0,
      tech: { weapons: 0, shields: 0, propulsion: 0, infrastructure: 0 },
      eliminated: !order.includes(id),
      shortage: 0,
      turnCount: id === g.player ? 1 : 0,
      specialUsed: false,
      sensorUntil: 0,
      discovered: [],
      intel: {},
    };
  for (const n of galaxyOf(g).nodes) {
    const owner = order.includes(n.startOwner as FactionId)
        ? (n.startOwner as FactionId)
        : null,
      style = FACTIONS[Math.floor(random(g) * 6)],
      types: ShipClass[] =
        n.stars === 1
          ? ["scout"]
          : n.stars === 2
            ? ["scout", "cruiser"]
            : ["cruiser", "cruiser"];
    g.systems[n.id] = {
      id: n.id,
      owner,
      mine: 0,
      research: 0,
      bastion: owner || n.stars === 4 ? 1 : 0,
      grid: 0,
      builtTurn: 0,
      guards: owner ? [] : types.map((t) => makeShip(g, t, style)),
      guardStyle: style,
      barrier: null,
      sabotage: null,
      ion: null,
      bastionHull: null,
      bastionShields: null,
    };
  }
  for (const f of order) {
    const role = f === g.player ? o.role : PREFERRED_ROLE[f];
    g.fleets.push({
      id: nextId(g, "fleet"),
      faction: f,
      system: HOME[f],
      ships: [makeShip(g, "scout", f), makeShip(g, "cruiser", f)],
      admiral: { role, xp: 0 },
      moves: maxMoves(role, 0),
      slipUsedTurn: 0,
    });
  }
  updateIntel(g);
  return g;
}
export function economicSummary(g: GameState, f: FactionId) {
  const ss = Object.values(g.systems).filter((s) => s.owner === f),
    base = Math.round(
      ss.reduce(
        (v, s) =>
          v +
          B.starCreditsPerTurn[
            String(nodesOf(g)[s.id].stars) as keyof typeof B.starCreditsPerTurn
          ],
        0,
      ) * (f === "reptiloids" ? 0.95 : 1),
    ),
    mines = ss.reduce((v, s) => v + B.mining.creditsBonus[s.mine], 0),
    upkeep = g.fleets
      .filter((fl) => fl.faction === f)
      .flatMap((fl) => fl.ships)
      .reduce((v, u) => v + B.ships[u.type].upkeep, 0);
  return {
    base,
    mines,
    upkeep,
    net: base + mines - upkeep,
    rp: Math.round(
      ss.reduce((v, s) => v + B.researchArray.researchBonus[s.research], 0) *
        (f === "alliance" ? 1.1 : 1),
    ),
    charge: ss.reduce(
      (v, s) => v + B.researchArray.anomalyChargeBonus[s.research],
      0,
    ),
  };
}
export function ledger(
  g: GameState,
  f: FactionId,
  key: string,
  amount = 0,
  currency: "credits" | "rp" | "charge" | "none" = "none",
  ref = "",
) {
  g.ledger.push({ turn: g.turn, faction: f, key, amount, currency, ref });
  if (g.ledger.length > 2400) g.ledger.splice(0, g.ledger.length - 2400);
}
function startTurn(g: GameState, f: FactionId) {
  const fs = g.factions[f],
    first = fs.turnCount === 0;
  fs.turnCount++;
  fs.specialUsed = false;
  const e = economicSummary(g, f);
  if (!first) {
    ledger(g, f, "income", e.base, "credits");
    if (e.mines) ledger(g, f, "mineIncome", e.mines, "credits");
    ledger(g, f, "upkeep", -e.upkeep, "credits");
    fs.shortage =
      fs.credits + e.net < 0
        ? Math.min(3, fs.shortage + 1)
        : fs.credits + e.net > 0
          ? 0
          : fs.shortage;
    fs.credits = Math.max(0, fs.credits + e.net);
    fs.rp += e.rp;
    const charge = Math.min(e.charge, 200 - fs.charge);
    fs.charge += charge;
    if (e.rp) ledger(g, f, "researchIncome", e.rp, "rp");
    if (charge) ledger(g, f, "chargeIncome", charge, "charge");
  }
  for (const s of Object.values(g.systems)) {
    for (const type of ["barrier", "sabotage", "ion"] as const) {
      const effect = s[type];
      if (effect?.faction === f && fs.turnCount >= effect.expires)
        s[type] = null;
    }
    if (s.owner === f && s.bastion && s.bastionHull !== null) {
      const m = combatBastion("station", f, s.bastion, s.grid);
      s.bastionHull = Math.min(
        m.maxHull,
        s.bastionHull +
          Math.round(m.maxHull * B.bastion.repairHullFraction[s.bastion]),
      );
      s.bastionShields = m.maxShields;
    }
  }
  for (const fl of g.fleets.filter((x) => x.faction === f)) {
    fl.moves = maxMoves(fl.admiral.role, fl.admiral.xp);
    const s = g.systems[fl.system];
    if (s.owner === f && s.bastion) {
      let hp = 0,
        sp = 0;
      for (const u of fl.ships) {
        const m = shipMax(u.type, f, fs.tech.shields),
          old = u.hull;
        sp += m.shields - u.shields;
        u.shields = m.shields;
        u.hull = Math.min(
          m.hull,
          u.hull +
            Math.round(
              m.hull *
                B.bastion.repairHullFraction[s.bastion] *
                (f === "okalani" ? 1.1 : 1),
            ),
        );
        hp += u.hull - old;
      }
      if (hp) ledger(g, f, "repair", hp, "none", s.id);
      if (sp) ledger(g, f, "shieldRestore", sp, "none", s.id);
    }
  }
  updateIntel(g);
}
export function advanceActor(g: GameState) {
  if (g.phase === "victory" || g.phase === "defeat") return;
  let guard = 0;
  do {
    g.actorIndex++;
    if (g.actorIndex >= g.order.length) {
      g.actorIndex = 0;
      g.turn++;
    }
    if (++guard > 6) throw new Error("No active faction");
  } while (g.factions[actor(g)].eliminated);
  g.actorActions = 0;
  startTurn(g, actor(g));
}
export function buildCost(
  g: GameState,
  f: FactionId,
  type: Building,
  level: number,
) {
  const costs =
    type === "mine"
      ? B.mining.upgradeCost
      : type === "research"
        ? B.researchArray.upgradeCost
        : type === "bastion"
          ? B.bastion.buildOrUpgradeCost
          : B.defenseGrid.cost;
  return Math.round(
    (costs[level] ?? Infinity) *
      (1 -
        B.technology.infrastructureCostDiscount[
          g.factions[f].tech.infrastructure
        ]) *
      (type === "bastion" && f === "xilur" ? 0.9 : 1),
  );
}
export const shipCost = (g: GameState, f: FactionId, t: ShipClass) =>
  Math.round(
    B.ships[t].cost *
      (1 -
        B.technology.infrastructureCostDiscount[
          g.factions[f].tech.infrastructure
        ]),
  );
export const technologyCost = (f: FactionId, t: Technology, level: number) =>
  Math.round(
    (B.technology.baseCostsByRank[level] ?? Infinity) *
      (PREFERENCES[f].includes(t) ? 0.85 : 1),
  );
export const commissionCost = (g: GameState, f: FactionId) =>
  B.admirals.commissionCosts[
    g.fleets.filter((fl) => fl.faction === f).length
  ] ?? Infinity;
function spend(
  g: GameState,
  f: FactionId,
  cost: number,
  key: string,
  ref = "",
) {
  check(g.factions[f].credits >= cost, "error.credits");
  g.factions[f].credits -= cost;
  ledger(g, f, key, -cost, "credits", ref);
}
function ownFleet(g: GameState, id: string, f: FactionId) {
  const fl = g.fleets.find((x) => x.id === id && x.faction === f);
  check(fl, "error.fleet");
  return fl;
}
function surrender(g: GameState, f: FactionId) {
  g.factions[f].eliminated = true;
  g.fleets = g.fleets.filter((fl) => fl.faction !== f);
  for (const s of Object.values(g.systems))
    if (s.owner === f) {
      s.owner = null;
      s.bastion = 0;
      s.grid = 0;
      s.guards = [];
      s.bastionHull = null;
      s.bastionShields = null;
      s.barrier = null;
      s.sabotage = null;
      s.ion = null;
    }
  ledger(g, g.player, "surrender", 0, "none", f);
  if (f === g.player) g.phase = "defeat";
  else if (
    g.order.filter((x) => x !== g.player).every((x) => g.factions[x].eliminated)
  )
    g.phase = "victory";
}
export function captureSystem(g: GameState, id: string, f: FactionId) {
  const s = g.systems[id],
    previous = s.owner;
  s.owner = f;
  s.bastion = 0;
  s.grid = 0;
  s.guards = [];
  s.builtTurn = 0;
  s.barrier = null;
  s.sabotage = null;
  s.ion = null;
  s.bastionHull = null;
  s.bastionShields = null;
  if (previous && g.factions[previous].intel[id])
    g.factions[previous].intel[id] = {
      owner: f,
      seen: g.turn,
      bastion: 0,
      fleetCount: null,
      ships: null,
    };
  if (previous === g.player) ledger(g, g.player, "lostSystem", 0, "none", id);
  ledger(g, f, "capture", 0, "none", id);
  const h = nodesOf(g)[id].startOwner as FactionId | null,
    eliminated: FactionId[] = [];
  if (h && h !== f && g.order.includes(h) && !g.factions[h].eliminated) {
    surrender(g, h);
    eliminated.push(h);
  }
  for (const fl of g.fleets.filter(
    (fl) => fl.system === id && fl.faction !== f,
  )) {
    const dest = Object.values(g.systems)
      .filter((s) => s.owner === fl.faction)
      .sort(
        (a, b) =>
          routeDistance(g, id, a.id) - routeDistance(g, id, b.id) ||
          a.id.localeCompare(b.id),
      )[0];
    if (dest) {
      fl.system = dest.id;
      fl.moves = 0;
    } else g.fleets = g.fleets.filter((x) => x.id !== fl.id);
  }
  return eliminated;
}
function startBattle(g: GameState, fl: Fleet, to: string) {
  const s = g.systems[to],
    defenders = g.fleets
      .filter((f) => f.system === to && f.faction !== fl.faction)
      .sort(
        (a, b) =>
          effectivePower(
            b.ships.map((u) =>
              combatShip(u, b.faction, g.factions[b.faction], b.admiral),
            ),
          ) -
            effectivePower(
              a.ships.map((u) =>
                combatShip(u, a.faction, g.factions[a.faction], a.admiral),
              ),
            ) || a.id.localeCompare(b.id),
      ),
    df = defenders[0],
    defFaction = df?.faction ?? s.owner,
    style = defFaction ?? s.guardStyle,
    attacker: CombatSide = {
      faction: fl.faction,
      style: fl.faction,
      fleetId: fl.id,
      admiral: clone(fl.admiral),
      stance: "neutral",
      units: fl.ships.map((u) =>
        combatShip(u, fl.faction, g.factions[fl.faction], fl.admiral),
      ),
    },
    defender: CombatSide = {
      faction: defFaction,
      style,
      fleetId: df?.id ?? null,
      admiral: df ? clone(df.admiral) : null,
      stance: "neutral",
      units: df
        ? df.ships.map((u) =>
            combatShip(u, style, g.factions[style], df.admiral),
          )
        : s.guards.map((u) => combatShip(u, style, null, null)),
    };
  if (s.bastion) {
    const unit = combatBastion("bastion-" + to, style, s.bastion, s.grid);
    if (s.bastionHull !== null) unit.hull = s.bastionHull;
    if (s.bastionShields !== null) unit.shields = s.bastionShields;
    if (s.sabotage) unit.shields = Math.round(unit.shields * 0.75);
    defender.units.push(unit);
  }
  if (s.ion)
    for (const u of defender.units)
      if (u.type !== "bastion") u.shields = Math.round(u.shields * 0.8);
  s.sabotage = null;
  s.ion = null;
  fl.moves = 0;
  const seed = Math.floor(random(g) * 4294967296) >>> 0;
  g.battle = {
    id: nextId(g, "battle"),
    system: to,
    origin: fl.system,
    attacker,
    defender,
    rng: seed,
    seed,
    round: 0,
    status: "active",
    events: [],
    initialAttacker: attacker.units.filter((u) => u.type !== "bastion").length,
    initialDefender: defender.units.filter((u) => u.type !== "bastion").length,
    attackerValue: attacker.units.reduce(
      (v, u) => v + (u.type === "bastion" ? 0 : B.ships[u.type].cost),
      0,
    ),
    defenderValue: defender.units.reduce(
      (v, u) => v + (u.type === "bastion" ? 0 : B.ships[u.type].cost),
      0,
    ),
    nebula: nodesOf(g)[to].visualVariant === "nebula",
    nocturnTriggered: false,
    difficulty: g.difficulty,
  };
  g.phase = "battle";
}
function special(
  g: GameState,
  f: FactionId,
  a: Extract<GameAction, { type: "special" }>,
) {
  const fs = g.factions[f],
    cost = B.specialAbilities[a.ability];
  check(!fs.specialUsed, "error.specialUsed");
  check(fs.charge >= cost, "error.charge");
  const s = a.system ? g.systems[a.system] : null,
    fl = a.fleet ? ownFleet(g, a.fleet, f) : null,
    adjacent =
      !!s &&
      Object.values(g.systems).some(
        (x) => x.owner === f && nodesOf(g)[x.id].neighbors.includes(s.id),
      );
  if (a.ability === "repairPulse") {
    check(fl && g.systems[fl.system].owner === f, "error.ownFleet");
    for (const u of fl.ships) {
      const m = shipMax(u.type, f, fs.tech.shields);
      u.hull = Math.min(m.hull, u.hull + Math.round(m.hull * 0.35));
      u.shields = m.shields;
    }
  }
  if (a.ability === "phaseBarrier") {
    check(s && s.owner === f, "error.ownSystem");
    s.barrier = { faction: f, expires: fs.turnCount + 1 };
  }
  if (a.ability === "slipstreamCorridor") {
    check(
      fl &&
        a.to &&
        g.systems[fl.system].owner === f &&
        g.systems[a.to]?.owner === f &&
        fs.discovered.includes(a.to) &&
        a.to !== fl.system &&
        routeDistance(g, fl.system, a.to) <= 3,
      "error.corridor",
    );
    check(fl.moves > 0 && fl.slipUsedTurn !== fs.turnCount, "error.moves");
    fl.system = a.to;
    fl.moves--;
    fl.slipUsedTurn = fs.turnCount;
  }
  if (a.ability === "sabotageProbe" || a.ability === "ionTempest") {
    check(
      s && s.owner !== f && fs.discovered.includes(s.id) && adjacent,
      "error.adjacentEnemy",
    );
    if (a.ability === "sabotageProbe") {
      check(s.bastion > 0, "error.bastion");
      s.sabotage = { faction: f, expires: fs.turnCount + 1 };
    } else s.ion = { faction: f, expires: fs.turnCount + 1 };
  }
  if (a.ability === "sensorBurst") fs.sensorUntil = fs.turnCount + 2;
  fs.charge -= cost;
  fs.specialUsed = true;
  ledger(g, f, a.ability, -cost, "charge", a.system ?? a.to ?? "");
}
/** Atomic transition: all validation occurs on a detached copy. Errors preserve the input state. */
export function act(state: GameState, a: GameAction): GameState {
  const g = clone(state);
  check(g.phase === "map", "error.phase");
  const f = actor(g),
    fs = g.factions[f];
  check(!fs.eliminated, "error.phase");
  if (a.type === "build") {
    const s = g.systems[a.system];
    check(s?.owner === f, "error.ownSystem");
    check(s.builtTurn !== fs.turnCount, "error.buildTurn");
    check(s[a.building] < (a.building === "grid" ? 2 : 3), "error.maxLevel");
    if (a.building === "mine") check(!s.research, "error.economicSlot");
    if (a.building === "research") check(!s.mine, "error.economicSlot");
    if (a.building === "grid") check(s.bastion > 0, "error.bastion");
    spend(
      g,
      f,
      buildCost(g, f, a.building, s[a.building] + 1),
      a.building,
      s.id,
    );
    s[a.building]++;
    s.builtTurn = fs.turnCount;
    if (a.building === "bastion") {
      s.bastionHull = null;
      s.bastionShields = null;
    }
  } else if (a.type === "ship") {
    const fl = ownFleet(g, a.fleet, f),
      s = g.systems[fl.system];
    check(s.owner === f && s.bastion > 0, "error.bastion");
    check(fl.ships.length < 7, "error.sevenShips");
    spend(g, f, shipCost(g, f, a.ship), "ship", a.ship);
    fl.ships.push(makeShip(g, a.ship, f));
  } else if (a.type === "commission") {
    const s = g.systems[a.system],
      fleets = g.fleets.filter((x) => x.faction === f);
    check(s?.owner === f && s.bastion > 0, "error.bastion");
    check(fleets.length < 3, "error.threeFleets");
    check(!fleets.some((x) => x.admiral.role === a.role), "error.commander");
    spend(g, f, commissionCost(g, f), "commission", a.role);
    g.fleets.push({
      id: nextId(g, "fleet"),
      faction: f,
      system: s.id,
      admiral: { role: a.role, xp: 0 },
      ships: [makeShip(g, "scout", f)],
      moves: maxMoves(a.role, 0),
      slipUsedTurn: 0,
    });
  } else if (a.type === "move") {
    const fl = ownFleet(g, a.fleet, f);
    check(fl.moves > 0, "error.moves");
    check(nodesOf(g)[fl.system].neighbors.includes(a.to), "error.neighbor");
    const s = g.systems[a.to];
    check(!s.barrier || s.owner === f, "error.barrier");
    if (f !== g.player && visibleSystems(g, g.player).has(a.to))
      ledger(g, g.player, "rivalMove", 0, "none", a.to);
    if (s.owner === f) {
      fl.system = a.to;
      fl.moves--;
      ledger(g, f, "move", 0, "none", a.to);
    } else if (
      s.guards.length ||
      s.bastion ||
      g.fleets.some((x) => x.system === s.id && x.faction !== f)
    )
      startBattle(g, fl, a.to);
    else {
      fl.system = a.to;
      fl.moves--;
      captureSystem(g, a.to, f);
      if (nodesOf(g)[a.to].type === "homeworld") fl.admiral.xp += 8;
    }
  } else if (a.type === "research") {
    const level = fs.tech[a.technology];
    check(level < 3, "error.maxLevel");
    const cost = technologyCost(f, a.technology, level + 1);
    check(fs.rp >= cost, "error.rp");
    fs.rp -= cost;
    fs.tech[a.technology]++;
    ledger(g, f, "technology", -cost, "rp", a.technology);
    if (a.technology === "shields")
      for (const fl of g.fleets.filter((x) => x.faction === f))
        for (const u of fl.ships) {
          const before = shipMax(u.type, f, level),
            after = shipMax(u.type, f, level + 1);
          u.hull = Math.round((u.hull / before.hull) * after.hull);
          u.shields = Math.round((u.shields / before.shields) * after.shields);
        }
  } else if (a.type === "special") special(g, f, a);
  else advanceActor(g);
  if (a.type !== "end") g.actorActions++;
  updateIntel(g);
  return g;
}
export function actionError(g: GameState, a: GameAction) {
  if (g.phase !== "map") return "error.phase";
  try {
    // History cannot affect order legality. Avoid copying large archived battle logs
    // when the interface checks several purchase/target buttons in one render.
    act({ ...g, reports: [], ledger: [] }, a);
    return null;
  } catch (e) {
    if (e instanceof RuleError) return e.key;
    throw e;
  }
}
export const playerBattleSide = (g: GameState): "attacker" | "defender" =>
  g.battle?.attacker.faction === g.player ? "attacker" : "defender";
export function canRetreat(g: GameState) {
  return (
    !!g.battle &&
    g.battle.status === "active" &&
    g.battle.attacker.faction === g.player &&
    g.systems[g.battle.origin].owner === g.player &&
    nodesOf(g)[g.battle.system].neighbors.includes(g.battle.origin)
  );
}
export function chooseStance(g: GameState, s: Stance) {
  check(g.battle?.status === "active", "error.phase");
  const copy = clone(g);
  copy.battle![playerBattleSide(copy)].stance = s;
  return copy;
}
export function battleRound(
  g: GameState,
  s: Stance,
  side?: "attacker" | "defender",
) {
  check(g.phase === "battle" && g.battle?.status === "active", "error.phase");
  const copy = clone(g);
  copy.battle = simulateRound(
    copy.battle!,
    s,
    side ?? playerBattleSide(copy),
  ).newState;
  return copy;
}
export function retreat(g: GameState) {
  check(canRetreat(g), "error.retreat");
  const copy = clone(g);
  copy.battle = retreatSalvo(copy.battle!).newState;
  return copy;
}
export function concludeBattle(state: GameState) {
  const g = clone(state),
    b = g.battle;
  check(b && b.status !== "active", "error.phase");
  g.phase = "map";
  const af = g.fleets.find((f) => f.id === b.attacker.fleetId),
    df = g.fleets.find((f) => f.id === b.defender.fleetId),
    aliveA = b.attacker.units.filter((u) => u.hull > 0 && u.type !== "bastion"),
    aliveD = b.defender.units.filter((u) => u.hull > 0 && u.type !== "bastion"),
    xpA = af?.admiral.xp ?? 0,
    xpD = df?.admiral.xp ?? 0,
    unitsToShips = (us: typeof aliveA): Ship[] =>
      us.map((u) => ({
        id: u.id,
        type: u.type as ShipClass,
        hull: u.hull,
        shields: u.shields,
      }));
  if (af) {
    af.ships = unitsToShips(aliveA);
    af.moves = 0;
  }
  if (df) {
    df.ships = unitsToShips(aliveD);
    df.moves = 0;
  } else g.systems[b.system].guards = unitsToShips(aliveD);
  const station = b.defender.units.find((u) => u.type === "bastion"),
    system = g.systems[b.system];
  if (station) {
    system.bastionHull = station.hull;
    system.bastionShields = station.shields;
    if (!station.hull) {
      system.bastion = 0;
      system.grid = 0;
      system.bastionHull = null;
      system.bastionShields = null;
    }
  }
  g.fleets = g.fleets.filter((f) => f.ships.length > 0);
  let surrendered: FactionId[] = [];
  if (b.status === "attacker" && af) {
    af.system = b.system;
    surrendered = captureSystem(g, b.system, af.faction);
  } else if (af && aliveA.length) {
    if (g.systems[b.origin].owner === af.faction) af.system = b.origin;
    else g.fleets = g.fleets.filter((f) => f.id !== af.id);
  }
  if (af && aliveA.length)
    af.admiral.xp +=
      4 * (b.initialDefender - aliveD.length) +
      (b.status === "attacker"
        ? 12 + (nodesOf(g)[b.system].type === "homeworld" ? 8 : 0)
        : b.status === "retreat"
          ? 3
          : 0);
  if (df && aliveD.length)
    df.admiral.xp +=
      4 * (b.initialAttacker - aliveA.length) +
      (b.status !== "attacker" ? 12 : 0);
  const lost = [...b.attacker.units, ...b.defender.units].filter(
    (u) => !u.hull && u.type !== "bastion",
  );
  const report: BattleReport = {
    id: b.id,
    turn: g.turn,
    system: b.system,
    attacker: b.attacker.faction!,
    defender: b.defender.faction,
    outcome: b.status,
    rounds: b.round,
    lostAttack: b.initialAttacker - aliveA.length,
    lostDefense: b.initialDefender - aliveD.length,
    creditsLost: lost.reduce(
      (v, u) => v + (u.type === "bastion" ? 0 : B.ships[u.type].cost),
      0,
    ),
    attackerXp: (af?.admiral.xp ?? 0) - xpA,
    defenderXp: (df?.admiral.xp ?? 0) - xpD,
    attackerRanks: [rank(xpA), rank(af?.admiral.xp ?? xpA)],
    defenderRanks: [rank(xpD), rank(df?.admiral.xp ?? xpD)],
    captured: b.status === "attacker",
    surrendered,
    engagement: b,
  };
  g.reports.push(report);
  if (g.reports.length > 200) g.reports.shift();
  for (let i = 0; i < g.reports.length - 20; i++)
    g.reports[i].engagement = null;
  ledger(g, b.attacker.faction!, "battle", 0, "none", b.system);
  g.battle = null;
  updateIntel(g);
  return g;
}
